import { HttpParameterCodec, HttpResponse } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscriber, forkJoin, of, firstValueFrom, from } from 'rxjs';
import { catchError, switchMap, switchMapTo, take, takeUntil } from 'rxjs/operators';
import { AudioWrapper, BookMarkInfo, BookMarkInfoWrapper, BookMarkOption, ConflictImage, InspectionImageCreate, InspectionImageListItem, UploadOption } from '../_objects/inspection-image';
import { IplexGSyncService } from '../_offline/services/iplex-g-sync.service';
import { BROWSER_TYPE, IMAGE_FILETYPE, PLATFORM, UPLOAD_RESULT_CODE } from '../app.constants';
import { InspectionImageStateService } from '../inspection-data-management/inspection-images-state.service';
import { DeviceFile } from '../inspection/dialogs/data-upload-dialog/save-iplex-g-images/save-iplex-g-images.component';
import { ConflictAction } from '../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.component';
import { ImageConflictDialogService } from '../inspection/dialogs/image-conflict-dialog/image-conflict-dialog.service';
import { UploadProgressDialogComponent } from '../inspection/dialogs/upload-progress-dialog/upload-progress-dialog.component';
import { UploadProgressDialogService } from '../inspection/dialogs/upload-progress-dialog/upload-progress-dialog.service';
import { UtilsService } from './_utils.service';
import { CommonSnackBarService } from './common-snack-bar.service';
import { InspectionImageService } from './http-api/inspection-image.service';
import { ImageCreationMapper } from '../_services/image-upload/image-creation.mapper';
import { InspectionFileValidator } from './image-upload/inspection-file.validator';
import { NetworkStatusService } from './network-status.service';
import { SpinnerService } from './spinner.service';
import { ExifReaderService } from "./exif-reader.service";
import { XmlReaderService } from "./xml-reader.service";
import { SubjectModelLocationsDbService } from "./database/subject-model-locations-db.service";
import { LeafLocation, SubjectModelLocationsSchema } from "../_offline/models/subjectModel-locations.schema";
import { MviqUtilsService } from "./mviq-utils.service";
import { getImageUploadPath } from '../_utils/file-system-utils';

export class FileResultList {
  [filePath: string]: string;
}

export class FileList {
  file: File;
  fileName: string;
  fullPath: string;
  fileStatus: string;
  locationId: string;

  constructor(file: File, fileName: string, fullPath: string, fileStatus: string, locationId: string) {
    this.file = file;
    this.fileName = fileName;
    this.fullPath = fullPath;
    this.fileStatus = fileStatus;
    this.locationId = locationId;
  }
}

@Injectable({
  providedIn: 'root'
})
export class ImageUploadOfflineService implements HttpParameterCodec, OnDestroy {
  private locationIdentifier: string;
  private _imageFileNameList: string[];
  private _imageFileResultList: FileResultList;
  private _fileList: FileList[] = [];
  private inspectionImages: InspectionImageListItem[] = [];

  private inspectionId: string;
  private isCancel = false;
  private progressDialogRef: MatDialogRef<UploadProgressDialogComponent>;
  private conflictList: ConflictImage[];
  private uploadSuccess$ = new Subject<boolean>();
  private onCancel$ = new Subject();
  private onDestroy$ = new Subject();
  private mviqTagFlagIsAvailable = false;

  constructor(
    private zone: NgZone,
    private dialog: MatDialog,
    private utilsService: UtilsService,
    private snackBar: CommonSnackBarService,
    private inspectionImageService: InspectionImageService,
    private networkStatusService: NetworkStatusService,
    private translate: TranslateService,
    private uploadProgressDialogService: UploadProgressDialogService,
    private imageConflictDialogService: ImageConflictDialogService,
    private iplexGSyncService: IplexGSyncService,
    private oscSpinnerService: SpinnerService,
    inspectionImageStateService: InspectionImageStateService,
    private exifReaderService: ExifReaderService,
    private xmlReaderService : XmlReaderService,
    private subjectModelLocationsDbService: SubjectModelLocationsDbService,
    private mviqUtilsService: MviqUtilsService
  ) {
    inspectionImageStateService
      .inspectionImages$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(images => this.inspectionImages = images);
  }

  get fileList() { return this._fileList; }
  get imageFileNameList() { return this._imageFileNameList; }
  get imageFileResultList() { return this._imageFileResultList; }

  get isDesktop() {
    return this.networkStatusService.isDesktop();
  }

  get isOffline() {
    return this.networkStatusService.isOffline;
  }

  get fileCount() {

    const excludedExtensions = [IMAGE_FILETYPE.EXTENSION_JSON, IMAGE_FILETYPE.EXTENSION_JSN, IMAGE_FILETYPE.EXTENSION_WAV];
    const filteredFileList = this._fileList.filter(file => {
      const lowerCaseFileName = file.fileName.toLowerCase();
      return !excludedExtensions.some(ext => lowerCaseFileName.endsWith(`.${ext}`));
    });

    return filteredFileList.length;
  }

  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(value: string): string {
    return encodeURIComponent(value);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(value: string): string {
    return decodeURIComponent(value);
  }

  ngOnDestroy(): void {
    this.onCancel$.next(undefined);
    this.onCancel$.unsubscribe();
    this.onDestroy$.next(undefined);
    this.onDestroy$.subscribe();
    this.uploadSuccess$.unsubscribe();
  }

  public get uploadSuccessObservable() {
    return this.uploadSuccess$.asObservable().pipe(takeUntil(this.onCancel$));
  }

  public uploadInit(inspectionId: string = null) {
    this.conflictList = [];
    this.isCancel = false;
    if (inspectionId) {
      this.inspectionId = inspectionId;
    }
  }

  public uploadFiles(inspectionId: string, locationId: string, files: DataTransferItemList): Observable<boolean> {
    this.inspectionId = inspectionId;
    this.locationIdentifier = locationId;

    this.upload(files);
    return this.uploadSuccessObservable;
  }

  public async loadFilesForFolder(inspectionId: string, files: File[]) {
    this.inspectionId = inspectionId;
    this._imageFileNameList = [];
    this._imageFileResultList = {};
    this._fileList = [];

    if (this.isChromeOrEdgeBrowser()) {
      for (const file of files) {
        const isUploadable = this.isUploadableFile(file.type, file.size, file.name);

        if (isUploadable) {
          let fullPath = file.webkitRelativePath;
          let filePath = this.getUploadPath(fullPath);

          if (file.webkitRelativePath == "") {
            // in case of Electron, for some, webkitRelativePath is not feeded
            filePath = fullPath = file.name;
          }

          this._fileList.push(new FileList(file, filePath, fullPath, UPLOAD_RESULT_CODE.BEFOREUPLOAD, inspectionId));
          if (!InspectionFileValidator.isValidJsonFile(file.name)) {
            this.imageFileNameList.push(fullPath);
          }

          this.imageFileResultList[fullPath] = UPLOAD_RESULT_CODE.BEFOREUPLOAD;
        }
      }
    }
  }

  private async upload(files: DataTransferItemList) {
    await this.loadFiles(this.inspectionId, files);

    if (this._fileList.length !== 0) {
      this.uploadFileList(this._fileList, this._imageFileNameList, this.locationIdentifier);
    }
  }

  // アップする画像ファイルを読み込む
  public async loadFiles(inspectionId: string, files: DataTransferItemList): Promise<boolean> {
    this.inspectionId = inspectionId;
    this._imageFileNameList = [];
    this._imageFileResultList = {};
    this._fileList = [];

    const obs = [];

    for (let i = 0; i < files.length; i++) {
      if (files[i]) {
        const entry = files[i].webkitGetAsEntry();

        if (entry.isFile) {
          obs.push(new Observable<any>(observer => this.traverseEntry(entry, observer)));
        } else {
          const dirEntry = entry as FileSystemDirectoryEntry;
          const reader = dirEntry.createReader();
          obs.push(new Observable<any>(observer => this.folderRead(reader, observer)));
        }
      }
    }

    return await forkJoin(obs)
      .toPromise()
      .then(_ => true)
      .catch(error => {
        console.log(error);
        return false;
      });
  }

  // アップロードする画像リストを生成する
  private traverseEntry(entry: any, observer: Subscriber<any>) {
    if (entry?.isFile) {
      this.createImageList4File(entry, observer);
    } else if (entry?.isDirectory) {
      this.createImageList4Directory(entry, observer);
    }
  }

  private isUploadableFile(filetype: string, fileSize: number, filename: string): boolean {
    const isUploadable = InspectionFileValidator.isValidInspectionFileType(filetype, filename) && fileSize > 0;
    return isUploadable;
  }

  // Generate a list of images to upload from a file.
  private createImageList4File(entry: any, observer: Subscriber<void>) {
    entry.file((file) => {
      this.zone.run(() => {
        const isUploadable = this.isUploadableFile(file.type, file.size, file.name);

        if (isUploadable) {
          const uploadPath = this.getUploadPath(entry.fullPath);

          this._fileList.push(new FileList(file, uploadPath, entry.fullPath, UPLOAD_RESULT_CODE.BEFOREUPLOAD, this.locationIdentifier));

          if (!InspectionFileValidator.isValidJsonFile(file.name)) {
            this._imageFileNameList.push(entry.fullPath);
          }

          this._imageFileResultList[entry.fullPath] = UPLOAD_RESULT_CODE.BEFOREUPLOAD;
        }
        observer.next();
        observer.complete();
      });
    });
  }

  // アップロードする画像リストをフォルダから生成する
  private createImageList4Directory(entry: any, observer: Subscriber<void>) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const reader = dirEntry.createReader();
    const obs = [];

    obs.push(new Observable<any>(observer => this.folderRead(reader, observer)));
    forkJoin(obs).subscribe(e => {
      observer.next();
      observer.complete();
    });
  }

  private folderRead(reader: any, observer: Subscriber<void>) {
    reader.readEntries((entries) => {
      const obs = entries.map(entry => new Observable<any>(innerObserver => this.traverseEntry(entry, innerObserver)));
      if (entries.length > 0) {
        obs.push(new Observable<any>(innerObserver => this.folderRead(reader, innerObserver)));
        forkJoin(obs).subscribe(_ => {
          observer.next();
          observer.complete();
        });
      } else {
        observer.next();
        observer.complete();
      }
    });
  }

  private getUploadPath(fullPath: string): string {
    return getImageUploadPath(fullPath);
  }

  public async uploadFileList(fileList: FileList[], fileNames: string[], locationIdentifier: string, uploadOption = UploadOption.None, inBackground: boolean = false): Promise<boolean> {
    this._fileList = fileList;
    this._imageFileNameList = fileNames;
    this.locationIdentifier = locationIdentifier;
    this.conflictList = [];

    const xmlFiles = this._fileList.filter(fileList =>
      InspectionFileValidator.isXMLFile(fileList.file.type)
    );

    const xmlFileSet = new Set(xmlFiles.map(xmlFile => xmlFile.file));

    // Remove FileList having an XML file
    this._fileList = this._fileList.filter(fileList => !xmlFileSet.has(fileList.file));

    fileList = fileList.filter(fileList => !xmlFileSet.has(fileList.file));

    this.mviqTagFlagIsAvailable = await firstValueFrom(from(this.mviqUtilsService.getMviqTagFlagIsAvailable()));

    this.isCancel = false;

    if (this.isChromeOrEdgeBrowser()) {
      for (let i = 0; i < fileList.length; i++) {
        if (!this.isUploadableFile(fileList[i].file.type, fileList[i].file.size, fileList[i].file.name)) {
          window.alert('The contents of the folder has changed.Please select a folder again');
          return false;
        }
      }

      const hasMainFiles = fileList.some(file => !InspectionFileValidator.isValidJsonFile(file.fileName) && !InspectionFileValidator.isWavAudioFile(file.fileName));

      // Do not open the dialog if only WAV/JSON files are present
      if (hasMainFiles) {
        const dialogRef = this.openProgressDialog(inBackground);

        dialogRef.afterClosed().subscribe(_ => this.cancelFileUpload());
        dialogRef.componentInstance.dialogClose.subscribe(_ => {
          if (!this.isErrorExists()) {
            dialogRef.close(true);
          } else {
            this.isCancel = true;
            this.uploadProgressDialogService.isError = true;
            this.uploadProgressDialogService.imageFileResultList = JSON.parse(JSON.stringify(this._imageFileResultList));
            this.onCancel$.next(undefined);
          }
        });
      }

      setTimeout(async () => {
        const mainFiles = fileList.filter(file => !InspectionFileValidator.isValidJsonFile(file.fileName) && !InspectionFileValidator.isWavAudioFile(file.fileName));
        const jsonFiles = fileList.filter(file => InspectionFileValidator.isValidJsonFile(file.fileName));
        const audioFiles = fileList.filter(file => InspectionFileValidator.isWavAudioFile(file.fileName));
        let result: Observable<any> = null;

        const inspectionLocations:SubjectModelLocationsSchema = await this.subjectModelLocationsDbService.getOneByInspectionId(this.inspectionId);
        const leafLocations :LeafLocation[] = this.mviqUtilsService.getLeafLocations(inspectionLocations?.locations);

        for (const file of mainFiles) {

          const isMviqModelImage = await this.exifReaderService.isMentorVisualIqModelImage(file.file);
          let mviqComment:string;

          if (isMviqModelImage) {
            const xmlFile = xmlFiles.find(xmlFile => this.utilsService.removeMimeType(xmlFile.fileName) === this.utilsService.removeMimeType(file.fileName));
            if (xmlFile) {
              mviqComment = await this.xmlReaderService.extractCommentFromXml(xmlFile.file);
            }
          }

          let observable;
          if (InspectionFileValidator.isValidJsonFile(file.fileName) || InspectionFileValidator.isWavAudioFile(file.fileName)) {
            continue;
          } else if (file.fileName.toLowerCase().endsWith(IMAGE_FILETYPE.EXTENSION_MP4)) {
            const json = jsonFiles.find(jsonFile => this.utilsService.removeMimeType(jsonFile.fileName) === this.utilsService.removeMimeType(file.fileName));
          if (json) {
            try {
              const jsonData: BookMarkInfo = await this.readJsonFile(json.file);
              let bookMarkOption: BookMarkOption[] = [];

              if (jsonData.markers)
                bookMarkOption = ImageCreationMapper.extractFromMarkers(jsonData.markers);

              if (jsonData.EVT)
                bookMarkOption = ImageCreationMapper.extractFromBrianMarkers(jsonData.EVT);

              observable = this.uploadProcessForOffline(file.file, file.fileName, file.fullPath, uploadOption, bookMarkOption, null, mviqComment, isMviqModelImage, leafLocations, this.mviqTagFlagIsAvailable);
            }
            catch (error) {
                console.error('Failed to retrieve Bookmark data', error);
                observable = this.uploadProcessForOffline(file.file, file.fileName, file.fullPath, uploadOption, null, null, mviqComment, isMviqModelImage, leafLocations, this.mviqTagFlagIsAvailable);
              }
            } else {
              observable = this.uploadProcessForOffline(file.file, file.fileName, file.fullPath, uploadOption, null, null, mviqComment, isMviqModelImage,leafLocations, this.mviqTagFlagIsAvailable);
            }
          } else {
            const audioFile = audioFiles.find(audioFile => this.utilsService.removeMimeType(audioFile.fileName) === this.utilsService.removeMimeType(file.fileName));
            observable = audioFile
              ? this.uploadProcessForOffline(file.file, file.fileName, file.fullPath, uploadOption, null, audioFile.file, mviqComment, isMviqModelImage,leafLocations, this.mviqTagFlagIsAvailable)
              : this.uploadProcessForOffline(file.file, file.fileName, file.fullPath, uploadOption,null,null,mviqComment, isMviqModelImage,leafLocations, this.mviqTagFlagIsAvailable);
          }

          if (observable) {
            const value = await observable.toPromise();
            result = result ? result.pipe(switchMap(() => of(value))) : of(value);
          }
        }

        if (result) {
          result.pipe(take(1)).subscribe(() => { });
        }
      }, 500);
    }

    return true;
  }

  async readJsonFile<T>(file: File): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const jsonData = event.target.result as string;
          const result = JSON.parse(jsonData) as T;
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };
      reader.readAsText(file);
    });
  }

  private isChromeOrEdgeBrowser(): boolean {
    const browserType = this.utilsService.getBrowserName();
    return browserType === BROWSER_TYPE.EDGE || browserType === BROWSER_TYPE.CHROME;
  }

  // ダイアログを表示
  private openProgressDialog(inBackground: boolean = false) {
    this.uploadProgressDialogService.init();
    this.uploadProgressDialogService.totalFileCount = this.fileCount;
    return this.progressDialogRef = this.dialog.open(UploadProgressDialogComponent, {
      width: UploadProgressDialogComponent.defaultDialogWidth,
      maxHeight: UploadProgressDialogComponent.defaultDialogMaxHeight,
      minHeight: UploadProgressDialogComponent.defaultDialogMinHeight,
      disableClose: true,
      data: { inBackground },
    });
  }

  public openReceiveProgressDialog(totalFileCount: number, totalFileSize: number, startTime: number) {
    this.uploadProgressDialogService.initReceiveMode(totalFileCount, totalFileSize, startTime, 'receiveFile');
    return this.progressDialogRef = this.dialog.open(UploadProgressDialogComponent, {
      width: UploadProgressDialogComponent.defaultDialogWidth,
      maxHeight: UploadProgressDialogComponent.defaultDialogMaxHeight,
      minHeight: UploadProgressDialogComponent.defaultDialogMinHeight,
      disableClose: true,
      data: {
        isTransfer: true
      }
    });
  }

  // 更新確認ダイアログを表示
  private openConflictDialog(conflictList: ConflictImage[]) {
    this.oscSpinnerService.hide(true);
    const dialogRef = this.imageConflictDialogService.open(conflictList);
    dialogRef.componentInstance.dialogAction.subscribe(conflictAction => {
      if (conflictAction.applyToAll) {
        this.applyImageResolutionToAll(conflictAction);
      }
      else {
        this.imageConflictDialogService.nextConflictImage();
        this.handleConflictResolution(conflictAction.image, conflictAction.uploadOption);
      }
    });

    dialogRef.componentInstance.cancelAction.subscribe(() => {
      this.imageConflictDialogService.close();
      this.progressDialogRef.close(false);
      this.uploadSuccess$.next(true);
    });

    return dialogRef;
  }

  private applyImageResolutionToAll(conflictAction: ConflictAction) {
    this.conflictList.forEach(conflictImage => this.handleConflictResolution(conflictImage, conflictAction.uploadOption));
    this.conflictList = [];
    this.imageConflictDialogService.close();
    this.progressDialogRef.close();
    this.uploadSuccess$.next(true);
  }

  private handleConflictResolution(conflictAction: ConflictImage, uploadOption: UploadOption) {
    if (uploadOption === UploadOption.Skip) {
      this.successUpload(conflictAction.fullPath);
    } else {
      this.uploadProcessForOffline(conflictAction.file, conflictAction.fileName, conflictAction.fullPath, uploadOption, null, null)
        .subscribe(
          () => { },
          error => error
        );
    }
  }

  private successUpload(fullPath: string): void {
    // 進捗を設定
    this.uploadProgressDialogService.completeFileCount += 1;
    this.uploadProgressDialogService.successFileCount += 1;
    this.uploadProgressDialogService.imageFileResultList[fullPath] = UPLOAD_RESULT_CODE.SUCCESS;

    // アップロード終了処理
    if (this.uploadProgressDialogService.completeFileCount === this.uploadProgressDialogService.totalFileCount) {
      if (this.uploadProgressDialogService.failureFileCount === 0) {
        this.isOffline
          ? this.snackBar.open(this.translate.instant('dataUploadDialog.successSaveSuccessfully'))
          : this.snackBar.open(this.translate.instant('dataUploadDialog.successUploadSuccessfully'));
        this.uploadProgressDialogService.isDisabledCancel = true;

        this.progressDialogRef.close();
        this.uploadSuccess$.next(true);
      } else {
        this.uploadProgressDialogService.isError = true;
        this.uploadProgressDialogService.imageFileResultList = this._imageFileResultList;
      }
    }
  }

  // アップロード失敗処理（1ファイル失敗毎に実行される）
  private failureUpload(fullPath: string): void {
    // 進捗を設定
    this.uploadProgressDialogService.completeFileCount += 1;
    this.uploadProgressDialogService.failureFileCount += 1;
    this.uploadProgressDialogService.imageFileResultList[fullPath] = UPLOAD_RESULT_CODE.FAILED;
    this.uploadProgressDialogService.failedFileNameList.push(fullPath);

    // アップロード終了処理
    if (this.uploadProgressDialogService.completeFileCount === this._fileList.length) {
      if (this.uploadProgressDialogService.failureFileCount !== 0) {
        this.snackBar.open('An error occurred upload image.');
        this.uploadProgressDialogService.isDisabledCancel = true;
        this.progressDialogRef.close();
      }
      this.uploadProgressDialogService.isError = true;
      this.uploadProgressDialogService.imageFileResultList = this._imageFileResultList;
    }
  }

  // ファイルアップロードのキャンセル処理
  private cancelFileUpload(): boolean {
    this.isCancel = true;
    this.uploadProgressDialogService.completeFileCount = 0;
    this.onCancel$.next(undefined);
    return true;
  }

  private isErrorExists(): boolean {
    let isError = false;
    for (const result in this._imageFileResultList) {
      if (this._imageFileResultList[result] === UPLOAD_RESULT_CODE.FAILED) {
        isError = true;
        break;
      }
    }
    return isError;
  }

  uploadProcessForOffline(file: File, fileName: string, fullPath: string, uploadOption: UploadOption, bookmarkOptions?: BookMarkOption[],
    audioFile: File = null, mviqComment?:string, isMviqImage= false, locations?: LeafLocation[], mviqTagFlagIsAvailable?:boolean ): Observable<any> {

    const inspectionImageCreate = new InspectionImageCreate();
    inspectionImageCreate.inspectionId = this.inspectionId;
    inspectionImageCreate.locationName = this.locationIdentifier;
    inspectionImageCreate.uploadOption = uploadOption;
    inspectionImageCreate.bookMarkOption = bookmarkOptions;
    inspectionImageCreate.audioFile = audioFile;

    return this.inspectionImageService
      .fileUploadRaw(inspectionImageCreate, file, fileName, null,  mviqComment, mviqTagFlagIsAvailable, isMviqImage)
      .pipe(takeUntil(this.onCancel$))
      .pipe(
        switchMap(event => {
          if (event instanceof HttpResponse) {
            this.successUpload(fileName);
          }
          return of(event);
        }),
        catchError(error => {
          if (error.status === 409) {

            if (uploadOption == UploadOption.Skip) {
              this.successUpload(fileName);
              return of(null);
            }

            const errorFileName = error.body.fileName;
            const subjectLocationId = error.body.subjectLocationId;
            const isWithoutExtension = error.body.isWithoutExtension;

            const conflictFile = this.fileList.find(_ => {
              return _.fullPath !== fullPath
                && _.locationId === subjectLocationId
                && _.fileName === errorFileName;
            })?.file;

            let conflictFilePath: string;
            if (!conflictFile) {
              const conflictImage = this.inspectionImages.find(_ => _.subjectLocationId === subjectLocationId && _.fileName === errorFileName);
              conflictFilePath = conflictImage?.thumbFilePath?.split('?')[0];
            }

            this.conflictList.push(new ConflictImage(file, fileName, fullPath, null, null, conflictFilePath, isWithoutExtension, conflictFile));

            if ((this.conflictList && this.conflictList.length > 0) && (!this.imageConflictDialogService.isOpened)) {
              this.openConflictDialog(this.conflictList);
            }
            return of(null);
          } else {
            console.log(error);
            this.failureUpload(fileName);
            return of(null);
          }
        })
      );
  }

  uploadProcessFromDevice(deviceFile: DeviceFile, inspectionId: string, isDeletedWithUploading: boolean, bookMarkInfoWrapper?: BookMarkInfoWrapper, audioWrapper?: AudioWrapper, uploadOption: UploadOption = null) {
    const file = deviceFile.file;
    const fileName = deviceFile.file.name;
    const inspectionImageCreate = new InspectionImageCreate();
    inspectionImageCreate.inspectionId = inspectionId;
    inspectionImageCreate.locationName = this.locationIdentifier;
    inspectionImageCreate.uploadOption = uploadOption ?? UploadOption.None;
    inspectionImageCreate.bookMarkOption = bookMarkInfoWrapper?.bookMarkInfo.markers;
    inspectionImageCreate.audioFile = audioWrapper?.file;

    if (this.uploadProgressDialogService.saveStartTime <= 0) {
      this.uploadProgressDialogService.saveStartTime = performance.now();
    }
    return this.inspectionImageService
      .fileUploadRaw(inspectionImageCreate, file, fileName)
      .pipe(takeUntil(this.onCancel$))
      .pipe(
        switchMap(event => {
          if (event instanceof HttpResponse) {
            this.successUpload(fileName);
          }

          if (isDeletedWithUploading) {
            const deleteMinFile$ = this.iplexGSyncService.deleteContentInSDcard(deviceFile.stat.filename);
            const deleteBmpFile$ = bookMarkInfoWrapper
              ? this.iplexGSyncService.deleteContentInSDcard(bookMarkInfoWrapper.filePath)
              : of(true);
            const deleteAudioFile$ = audioWrapper
              ? this.iplexGSyncService.deleteContentInSDcard(audioWrapper.filePath)
              : of(true);

            return deleteMinFile$
              .pipe(switchMapTo(deleteBmpFile$))
              .pipe(switchMapTo(deleteAudioFile$))
              .pipe(switchMapTo(of(event)));
          }

          return of(event);
        }),
        catchError(error => {
          if (error.status === 409) {
            const subjectLocationId = error.body.subjectLocationId;
            const isWithoutExtension = error.body.isWithoutExtension;
            const conflictImage = this.inspectionImages.find(_ => _.subjectLocationId === subjectLocationId && _.fileName === fileName);
            const conflictFilePath = conflictImage?.thumbFilePath?.split('?')[0];
            this.conflictList.push(new ConflictImage(deviceFile.file, fileName, deviceFile.stat.basename, null, null, conflictFilePath, isWithoutExtension, file));

            if ((this.conflictList && this.conflictList.length > 0) && (!this.imageConflictDialogService.isOpened)) {
              this.oscSpinnerService.hide(true);
              const dialogRef = this.imageConflictDialogService.open(this.conflictList, { applyToAllEnabled: false, cancelEnabled: false });

              dialogRef.componentInstance.dialogAction.subscribe(conflictAction => {

                this.imageConflictDialogService.nextConflictImage();

                if (conflictAction.uploadOption === UploadOption.Skip) {
                  this.successUpload(conflictAction.image.fullPath);
                } else {
                  this.uploadProcessFromDevice(deviceFile, inspectionId, isDeletedWithUploading, bookMarkInfoWrapper, audioWrapper, conflictAction.uploadOption)
                    .subscribe(
                      () => { },
                      error => error
                    );
                }
              });

              dialogRef.componentInstance.cancelAction.subscribe(_ => {
                this.imageConflictDialogService.close();
                this.progressDialogRef.close(false);
                this.uploadSuccess$.next(true);
              });
            }
            return of(null as HttpResponse<{}>);
          } else {
            console.log(error);
            this.failureUpload(fileName);
            return of(null as HttpResponse<{}>);
          }
        })
      );
  }

}
